import React from 'react';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, alpha,
  Typography, Avatar, Stack, IconButton, useTheme, useMediaQuery,
} from '@mui/material';
import {
  TrendingUp, Users, ShoppingBag, FileText, Layers, LayoutGrid, Tag, Briefcase,
  MessageSquare, Globe, Map, MapPin, Zap, BarChart3, PieChart, Settings as SettingsIcon,
  FileSpreadsheet, X as CloseIcon, LogOut, Target, IndianRupee, Workflow, BookOpen, PartyPopper,
  ClipboardCheck, Handshake, Wallet, SlidersHorizontal, PhoneCall, BadgeIndianRupee,
  HelpCircle, MessageCircle, Newspaper,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../redux/slices/authSlice';
import { useFeatureFlags } from '../contexts/FeatureFlagsProvider';

export const DRAWER_WIDTH = 280;

const sections = [
  {
    label: 'Analytics',
    items: [
      { text: 'Dashboard', icon: TrendingUp, path: '/dashboard' },
      { text: 'Analytics', icon: BarChart3, path: '/analytics' },
      { text: 'Marketing Steps', icon: Target, path: '/marketing-steps' },
      { text: 'Revenue', icon: IndianRupee, path: '/revenue' },
      { text: 'WBR Report', icon: FileSpreadsheet, path: '/wbr' },
      { text: 'Conversations', icon: PieChart, path: '/messages' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { text: 'SOPs', icon: ClipboardCheck, path: '/sop' },
    ],
  },
  {
    label: 'Commerce',
    items: [
      { text: 'Users', icon: Users, path: '/users' },
      { text: 'Follow-ups', icon: PhoneCall, path: '/follow-ups' },
      { text: 'Businesses', icon: Briefcase, path: '/businesses' },
      { text: 'Products', icon: ShoppingBag, path: '/products' },
      { text: 'RFQs (Quotes)', icon: FileText, path: '/rfq' },
      { text: 'Plans', icon: FileText, path: '/plans' },
      { text: 'Subscribers', icon: BadgeIndianRupee, path: '/subscribers' },
    ],
  },
  {
    label: 'Partner Program',
    items: [
      { text: 'Partners', icon: Handshake, path: '/partners' },
      { text: 'Payouts', icon: Wallet, path: '/partner-payouts' },
      { text: 'Partner Settings', icon: SlidersHorizontal, path: '/partner-settings' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { text: 'Categories', icon: LayoutGrid, path: '/categories' },
      { text: 'Sub Categories', icon: Layers, path: '/sub-categories' },
      { text: 'Brands', icon: Tag, path: '/brands' },
    ],
  },
  {
    label: 'Engagement',
    items: [
      { text: 'WhatsApp', icon: WhatsAppIcon, path: '/whatsapp' },
      { text: 'Buyer & Seller Chat', icon: MessageCircle, path: '/buyer-seller-chat' },
      { text: 'Help & Support', icon: HelpCircle, path: '/help-requests' },
      { text: 'General Enquiries', icon: MessageSquare, path: '/enquiries' },
      { text: 'Customer Enquiry Data', icon: Users, path: '/customer-enquiry-data' },
      { text: 'Custom Pages', icon: FileText, path: '/custom-pages' },
      { text: 'Blogs', icon: BookOpen, path: '/blogs' },
      { text: 'News & PR', icon: Newspaper, path: '/news-pr' },
      { text: 'Festivals', icon: PartyPopper, path: '/festivals' },
      { text: 'Automation', icon: Zap, path: '/automation' },
      { text: 'Automation Center', icon: Workflow, path: '/automation-center' },
    ],
  },
  {
    label: 'Geography',
    items: [
      { text: 'Countries', icon: Globe, path: '/countries' },
      { text: 'States', icon: Map, path: '/states' },
      { text: 'Cities', icon: MapPin, path: '/cities' },
    ],
  },
  {
    label: 'System',
    items: [
      { text: 'Settings', icon: SettingsIcon, path: '/settings' },
    ],
  },
];

const NavItem = ({ item, active, onClick }) => {
  const theme = useTheme();
  const Icon = item.icon;
  return (
    <ListItem disablePadding sx={{ display: 'block', mb: 0.35 }}>
      <ListItemButton
        onClick={onClick}
        sx={{
          minHeight: 40,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          mx: 1.5,
          width: 'auto',
          position: 'relative',
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
          backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
          fontWeight: active ? 700 : 500,
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: active
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.text.primary, 0.04),
            color: active ? theme.palette.primary.main : theme.palette.text.primary,
            transform: 'translateX(2px)',
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
          ...(active && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -6,
              top: '50%',
              transform: 'translateY(-50%)',
              height: 20,
              width: 3.5,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main,
            },
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: 1.5,
            color: active ? theme.palette.primary.main : 'inherit',
            justifyContent: 'center',
            transition: 'color 0.15s ease',
          }}
        >
          {item.text === 'WhatsApp' ? (
            <Icon sx={{ fontSize: 19 }} />
          ) : (
            <Icon size={19} strokeWidth={active ? 2.4 : 1.9} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            fontSize: '0.84rem',
            fontWeight: active ? 700 : 500,
            lineHeight: 1.4,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const SidebarContent = ({ onNavigate, onClose, showClose = false }) => {
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isPathEnabled } = useFeatureFlags() || {};
  const { user } = useSelector((s) => s.auth) || {};

  // Hide tabs for modules disabled in System settings, then drop empty sections.
  const visibleSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (isPathEnabled ? isPathEnabled(item.path) : true)),
    }))
    .filter((section) => section.items.length > 0);
  const displayName =
    user?.name ||
    `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
    'Administrator';
  const role = user?.role || 'Super admin';
  const initials =
    displayName
      .split(' ')
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'A';

  const handleLogout = () => {
    dispatch(logout());
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.replace('/signin');
  };

  return (
    <>
      {/* Brand block */}
      <Box
        sx={{
          px: 2.5,
          py: 2.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          onClick={() => onNavigate('/dashboard')}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: '#fff',
              fontWeight: 800,
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
              flexShrink: 0,
            }}
          >
            RB
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }} noWrap>
              Reseller Bazzar
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600 }} noWrap>
              Admin Panel · v2.0
            </Typography>
          </Box>
        </Box>
        {showClose && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
            aria-label="Close navigation"
          >
            <CloseIcon size={20} />
          </IconButton>
        )}
      </Box>

      {/* Nav sections */}
      <Box
        sx={{
          overflowY: 'auto',
          flex: 1,
          pt: 1,
          pb: 2,
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.divider, 0.5),
            borderRadius: 4,
          },
        }}
      >
        {visibleSections.map((section) => (
          <Box key={section.label} sx={{ mb: 1.5 }}>
            <Typography
              sx={{
                px: 3,
                py: 1,
                fontSize: 11,
                fontWeight: 800,
                color: 'text.disabled',
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {section.label}
            </Typography>
            <List sx={{ px: 0, py: 0 }}>
              {section.items.map((item) => (
                <NavItem
                  key={item.text}
                  item={item}
                  active={location.pathname === item.path}
                  onClick={() => onNavigate(item.path)}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer profile card */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.main',
              color: '#fff',
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 700 }}>
              {displayName}
            </Typography>
            <Typography noWrap sx={{ fontSize: 11, color: 'text.secondary' }}>
              {role}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleLogout}
            aria-label="Logout"
            sx={{ color: 'error.main' }}
          >
            <LogOut size={16} />
          </IconButton>
        </Stack>
      </Box>
    </>
  );
};

const Sidebar = ({ mobileOpen, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  const paperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    // Keep the brand block clear of the notch and the profile card clear of the
    // home indicator when the drawer covers the full screen on an installed PWA.
    pt: 'env(safe-area-inset-top, 0px)',
    pb: 'env(safe-area-inset-bottom, 0px)',
    pl: 'env(safe-area-inset-left, 0px)',
  };

  return (
    <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': paperSx,
        }}
      >
        <SidebarContent onNavigate={handleNavigate} onClose={onClose} showClose />
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': paperSx,
        }}
        open
      >
        <SidebarContent onNavigate={handleNavigate} onClose={onClose} />
      </Drawer>
    </Box>
  );
};

export default Sidebar;
